/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { MonthView } from '#PRIVATE_MonthView';
import type { MonthsGridView, YearsGridView } from '#PRIVATE_MonthYearGridView';
import { useFocusWithin } from '#hooks/UNSAFE_useFocusWithin';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import {
  constrainCalendarDate,
  getCalendarDateToday,
  isCalendarDateBefore,
  type CalendarDateRequired
} from '#utils/UNSAFE_calendarDateUtils';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { ComponentProps, useCallback, useMemo } from 'preact/compat';
import { useDatePickerState } from './useDatePickerState';

/**
 * A map that specifies the selection of the header toggle button based on the
 * view we are in.
 */
const headerToggleSelection = {
  monthView: 'none',
  monthsGridView: 'month',
  yearsGridView: 'year'
} as const;

type MonthViewProps = ComponentProps<typeof MonthView>;
type MonthsGridViewProps = ComponentProps<typeof MonthsGridView>;
type YearsGridViewProps = ComponentProps<typeof YearsGridView>;

type UseDatePickerProps = {
  isReadonly?: boolean;
  max?: CalendarDateRequired;
  min?: CalendarDateRequired;
  todayTimeZone?: Intl.DateTimeFormatOptions['timeZone'];
  value?: CalendarDateRequired;
  onCommit: (detail: ValueUpdateDetail<CalendarDateRequired>) => void;
};

/**
 * A custom hook that adds functionality to the DatePicker component.
 */
export const useDatePicker = ({
  isReadonly,
  max,
  min,
  todayTimeZone,
  value,
  onCommit
}: UseDatePickerProps) => {
  // Check for errors first
  if (min && max && isCalendarDateBefore(max, min)) {
    throw new Error('DatePicker: min must be equal or less than max.');
  }

  const todaysDate = getCalendarDateToday(todayTimeZone);

  // We need to make sure that this initialFocusableDate is within the min/max range.
  // When there is no min or max set, we draw the month of the value if value is set, otherwise we draw today's month.
  // If the initialFocusableDate is not within the min/max range, then we constrain the calendar date by changing the month to draw
  // so that the initialFocusableDate is the min date if the initialFocusableDate is < min or the max month if the initialFocusableDate is > max.
  const initialFocusableDate = useMemo(
    () => constrainCalendarDate(value ?? todaysDate, min, max),
    [value, todaysDate, min, max]
  );

  const { state, actions } = useDatePickerState({ initialFocusableDate });

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const accessibleLabels = {
    next: translations.datePicker_next(),
    previous: translations.datePicker_prev(),
    goToToday: translations.datePicker_goToToday(),
    today: translations.datePicker_today()
  };

  const handlers = {
    /**
     * Handler for DatePickerHeader.onMonthToggle.
     * This is used for toggling between the MonthView and
     * MonthsGridView.
     */
    handleMonthToggle: useCallback(() => {
      if (state.currentView !== 'monthsGridView') {
        actions.monthsGridView(undefined);
      } else {
        actions.monthView(undefined);
      }
    }, [actions, state.currentView]),

    /**
     * Handler for DatePickerHeader.onYearToggle.
     * This is used for toggling between the MonthView and
     * YearsGridView.
     */
    handleYearToggle: useCallback(() => {
      if (state.currentView !== 'yearsGridView') {
        actions.yearsGridView(undefined);
      } else {
        actions.monthView(undefined);
      }
    }, [actions, state.currentView]),

    /**
     * Handler for DatePickerHeader.onTodayNavigation.
     * This is used for navigating to today in the MonthView.
     */
    handleTodayNavigation: useCallback(() => {
      actions.goToToday(todaysDate);
    }, [actions, todaysDate]),

    /**
     * Handler for DatePickerHeader.onPreviousNavigation.
     * This is used for navigating to the previous month in MonthView
     * and previous decade in YearsGridView.
     */
    handlePreviousNavigation: useCallback(() => {
      if (state.currentView === 'monthView') {
        return actions.previousMonth(state.focusableDate);
      }

      if (state.currentView === 'yearsGridView') {
        return actions.previousDecade({ year: state.focusableYear });
      }
    }, [actions, state.currentView, state.focusableDate, state.focusableYear]),

    /**
     * Handler for DatePickerHeader.onNextNavigation.
     * This is used for navigating to the next month in MonthView
     * and next decade in YearsGridView.
     */
    handleNextNavigation: useCallback(() => {
      if (state.currentView === 'monthView') {
        return actions.nextMonth(state.focusableDate);
      }

      if (state.currentView === 'yearsGridView') {
        return actions.nextDecade({ year: state.focusableYear });
      }
    }, [actions, state.currentView, state.focusableDate, state.focusableYear]),

    /**
     * Handler for MonthView.onAction.
     * This is used for selecting a date in MonthView.
     */
    handleMonthViewAction: useCallback<MonthViewProps['onAction']>(
      (payload) => {
        if (payload.date === undefined || isReadonly) return;
        actions.daySelected(payload.date);
        // We purposely send onCommit even if the value is the same.
        // The InputDatePicker closes the datepicker when the user commits,
        // even if it is the already selected date.
        onCommit?.({ previousValue: value, value: payload.date });
      },
      [actions, isReadonly, onCommit, value]
    ),

    /**
     * Handler for MonthView.onNavigation.
     * This is used for navigating in the MonthView like moving around the
     * dates in the month and going to the prev/next month/year.
     */
    handleMonthViewNavigation: useCallback<MonthViewProps['onNavigation']>(
      (detail) => {
        switch (detail.direction) {
          case 'previousMonth':
            return actions.previousMonthAndFocus(detail.date);
          case 'nextMonth':
            return actions.nextMonthAndFocus(detail.date);
          case 'today':
            return actions.goToToday(todaysDate);
          default:
            return actions[detail.direction](detail.date);
        }
      },
      [actions, todaysDate]
    ),

    /**
     * Handler for MonthsGridView.onAction.
     * This is used for selecting a month in MonthsGridView and navigating to
     * the selected month in MonthView.
     */
    handleMonthsGridViewAction: useCallback<MonthsGridViewProps['onAction']>(
      (detail) => {
        actions.monthSelected(detail);
      },
      [actions]
    ),

    /**
     * Handler for MonthsGridView.onNavigation.
     * This is used for navigating through the MonthsGridView.
     */
    handleMonthsGridViewNavigation: useCallback<MonthsGridViewProps['onNavigation']>(
      (detail) => {
        switch (detail.direction) {
          case 'down':
            return actions.nextMonthRowInGridView({ month: detail.month });
          case 'next':
            return actions.nextMonthInGridView({ month: detail.month });
          case 'previous':
            return actions.previousMonthInGridView({ month: detail.month });
          case 'up':
            return actions.previousMonthRowInGridView({ month: detail.month });
        }
      },
      [actions]
    ),

    /**
     * Handler for YearsGridView.onAction.
     * This is used for selecting a year in YearsGridView and navigating to
     * the selected year in MonthView.
     */
    handleYearsGridViewAction: useCallback<YearsGridViewProps['onAction']>(
      (detail) => {
        actions.yearSelected(detail);
      },
      [actions]
    ),

    /**
     * Handler for YearsGridView.onNavigation.
     * This is used for navigating through the YearsGridView.
     */
    handleYearsGridViewNavigation: useCallback<YearsGridViewProps['onNavigation']>(
      (detail) => {
        switch (detail.direction) {
          case 'down':
            return actions.nextYearRowInGridView({ year: detail.year });
          case 'next':
            return actions.nextYearInGridView({ year: detail.year });
          case 'nextDecade':
            return actions.nextDecadeAndFocus({ year: detail.year });
          case 'previous':
            return actions.previousYearInGridView({ year: detail.year });
          case 'previousDecade':
            return actions.previousDecadeAndFocus({ year: detail.year });
          case 'up':
            return actions.previousYearRowInGridView({ year: detail.year });
        }
      },
      [actions]
    )
  };

  const { focusProps } = useFocusWithin({
    // keep the state in sync with the actual focus state
    onBlurWithin: useCallback(() => actions.viewBlur(undefined), [actions]),
    onFocusWithin: useCallback(() => actions.viewFocus(undefined), [actions])
  });

  return {
    accessibleLabels,
    actions,
    handlers,
    state,
    todaysDate,
    toggleSelection: headerToggleSelection[state.currentView],
    viewContainerProps: { ...focusProps }
  };
};
